import { inject, isDevMode } from '@angular/core'
import { ActivatedRouteSnapshot, CanActivateChildFn, CanActivateFn, Route, Router, RouterStateSnapshot, UrlTree } from '@angular/router'
import { Observable, of } from 'rxjs'
import { first, map, switchMap } from 'rxjs/operators'
import { PermissionsService } from '@shared/services/permissions.service'
import { AuthService } from '@app/modules/auth/auth.service'
import { PermissionSourceType } from '../types/permissions.source'
import { User } from '@shared/models/user.model'
export const permissionGuard: CanActivateFn = (next: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | UrlTree> => {
  const router = inject(Router)
  const permissionsService = inject(PermissionsService)
  const authService = inject(AuthService)
  const permissionType = next.data.permissionGuard as { client: PermissionSourceType; admin: PermissionSourceType }
  if (!permissionType) {
    return of(true)
  }
  return authService.currentUser$.pipe(
    first(),
    map((user: User): PermissionSourceType => {
      const permissionTypeDependsOnTeam = user.isClient() ? permissionType.client : permissionType.admin
      const permission: PermissionSourceType = typeof permissionType === 'string' ? permissionType : permissionTypeDependsOnTeam
      return permission
    }),
    switchMap((permission: PermissionSourceType): Observable<boolean> => permissionsService.hasPermissionAsync(permission)),
    switchMap((hasPermission: boolean): Observable<boolean | UrlTree> => {
      if (hasPermission) return of(true)
      // In dev: Go API migration may return a partial or renamed permission set — bypass any
      // failed check to keep the app functional during migration. Prod/stage still enforce.
      if (isDevMode()) {
        return of(true)
      }
      const url = resolveNearRoute(next, permissionsService)
      // Prevent redirect loop: if the resolved fallback is the current URL — or an ancestor of it
      // whose empty-path child would redirect straight back into this denied subtree — redirecting
      // there oscillates forever. Fall back to the user's own area root (/admin, /client, /reseller),
      // derived from the current URL — never a hardcoded /admin, which a client/reseller cannot enter.
      const normalizedUrl = url.startsWith('/') ? url : `/${url}`
      if (normalizedUrl === state.url || state.url.startsWith(`${normalizedUrl}/`)) {
        const areaRoot = `/${state.url.split('/')[1] ?? ''}`
        return of(router.parseUrl(areaRoot))
      }
      return of(router.parseUrl(url))
    }),
  )
}
export const permissionChildGuard: CanActivateChildFn = (childRoute: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
  return permissionGuard(childRoute, state)
}
function resolveNearRoute(nextRoute: ActivatedRouteSnapshot, permissionsService: PermissionsService): string {
  let availableRoute = nextRoute
  while (!availableRoute?.routeConfig?.children?.length) {
    availableRoute = availableRoute.parent
  }
  // Skip parametric paths (e.g. ':id', 'users/:id') — they cannot be used as fallback URLs without a real param value
  const hasParamSegment = (path: string): boolean => path.split('/').some((segment) => segment.startsWith(':'))
  const isValidRoute = ({ path, data }: Route): boolean => !!path && !!data && !hasParamSegment(path)
  const children = availableRoute?.routeConfig?.children?.filter(isValidRoute)
  if (!children.length) return '/'
  const goToRoute = children.find((route: Route): boolean =>
    permissionsService.hasPermissionSync(route.data.permissionGuard as PermissionSourceType),
  )
  const gotToRoot = pathFromRoot(availableRoute)
  return goToRoute ? `${gotToRoot}/${goToRoute.path}` : gotToRoot
}
function pathFromRoot(closestAvailableRoute: ActivatedRouteSnapshot): string {
  const pathFromRoot = closestAvailableRoute.pathFromRoot
  const toStringRoute = (route: ActivatedRouteSnapshot): string => route.url.map(String).join('/')
  return pathFromRoot.map(toStringRoute).filter(Boolean).join('/')
}
